import { useEffect, useState } from 'react';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import styles from './AuthSuccessPage.module.css';

export default function AuthSuccessPage() {
  const [status, setStatus] = useState<'sending' | 'success' | 'error'>('sending');
  const [message, setMessage] = useState('Sending authentication data to extension...');
  const { getToken, user, isAuthenticated } = useKindeAuth();

  useEffect(() => {
    const sendAuthToExtension = async () => {
      try {
        // Wait for authentication to complete
        if (!isAuthenticated) {
          setMessage('Waiting for authentication...');
          return;
        }

        // Get the access token from Kinde
        const accessToken = await getToken();

        if (!accessToken || !user) {
          throw new Error('Missing authentication data');
        }

        // Get extension IDs from environment variable (comma-separated for multiple IDs)
        const extensionIdsStr = import.meta.env.VITE_EXTENSION_ID || 'dmpabeapmcligpllfikofbgpabagbhda';
        const extensionIds = extensionIdsStr.split(',').map((id: string) => id.trim());

        console.log('Attempting to send auth to extension(s):', extensionIds);
        console.log('Access token received:', accessToken ? 'Yes' : 'No');
        console.log('User data received:', user);

        // Store auth data in localStorage for the website
        localStorage.setItem('access_token', accessToken);
        localStorage.setItem('user', JSON.stringify(user));

        // Send message to extension(s)
        if (typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.sendMessage) {
          console.log('Sending message to extension(s) via chrome.runtime.sendMessage');

          let successCount = 0;
          let errorCount = 0;

          extensionIds.forEach((extensionId: string) => {
            chrome.runtime.sendMessage(
              extensionId,
              {
                type: 'AUTH_SUCCESS',
                data: {
                  accessToken,
                  user,
                },
              },
              (response) => {
                if (chrome.runtime.lastError) {
                  console.error(`Extension ${extensionId} communication error:`, chrome.runtime.lastError.message);
                  errorCount++;

                  // If all extensions failed, show error
                  if (errorCount === extensionIds.length) {
                    setStatus('error');
                    setMessage(
                      `Could not communicate with any extension. Tried IDs: ${extensionIds.join(', ')}`
                    );
                  }
                } else if (response?.success) {
                  console.log(`Extension ${extensionId} acknowledged authentication`);
                  successCount++;

                  // If at least one extension succeeded, mark as success
                  if (successCount === 1) {
                    setStatus('success');
                    setMessage('Authentication successful! Redirecting to subscription page...');
                    setTimeout(() => {
                      window.location.href = '/subscription';
                    }, 2000);
                  }
                } else {
                  console.warn(`Extension ${extensionId} response:`, response);
                  errorCount++;

                  // If all extensions failed, show error
                  if (errorCount === extensionIds.length) {
                    setStatus('error');
                    setMessage('Extension(s) did not acknowledge the authentication.');
                  }
                }
              }
            );
          });
        } else {
          console.log('chrome.runtime not available, falling back to postMessage');
          console.log('window.opener:', window.opener);

          // Fallback: Use postMessage for cross-origin communication
          if (window.opener) {
            window.opener.postMessage(
              {
                type: 'AUTH_SUCCESS',
                data: {
                  accessToken,
                  user,
                },
              },
              '*' // In production, specify the extension origin
            );

            setStatus('success');
            setMessage('Authentication successful! Redirecting to subscription page...');
            setTimeout(() => {
              window.location.href = '/subscription';
            }, 2000);
          } else {
            setStatus('error');
            setMessage('No way to communicate with extension. Please ensure the extension opened this window.');
          }
        }
      } catch (error) {
        console.error('Auth success error:', error);
        setStatus('error');
        setMessage(
          error instanceof Error
            ? error.message
            : 'An error occurred while processing authentication.'
        );
      }
    };

    sendAuthToExtension();
  }, [isAuthenticated, user, getToken]);

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        {status === 'sending' && (
          <>
            <div className={styles.spinner}></div>
            <h1>Authenticating...</h1>
            <p>{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className={styles.successIcon}>✓</div>
            <h1>Success!</h1>
            <p>{message}</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className={styles.errorIcon}>✕</div>
            <h1>Error</h1>
            <p>{message}</p>
            <button onClick={() => window.close()} className={styles.closeButton}>
              Close Window
            </button>
          </>
        )}
      </div>
    </div>
  );
}
